import { Box, Button, Grid, IconButton, Stack, TextField } from '@mui/material'
import {
  RailVisitGeneralInformationFormStore,
  RailVisitVoyageTripIdType,
} from '@planning/pages/RailVisit/stores/RailVisitGeneralInformationFormStore'
import { useTranslate } from '@tolgee/react'
import { BinIcon, PlusIcon } from '@tom-ui/ui'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect } from 'react'

type VoyageGroupProps = {
  index: number
  voyage: RailVisitVoyageTripIdType
  onDelete?: () => void
}

const VoyageGroup: FC<VoyageGroupProps> = observer(props => {
  const { index, voyage, onDelete } = props
  const { t } = useTranslate()

  const tripIdHelperText = (error?: string) => {
    if (error === 'duplicateTripId') return t('duplicateTripId', 'Trip ID must be unique.')
    return t('fieldIsRequired', 'Field is required.')
  }

  return (
    <Box data-cy={`rail-visit-voyage-group-${index}`}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 5 }}>
          <TextField
            {...voyage.inboundNumber.props}
            onBlur={voyage.inboundNumber.validate}
            fullWidth
            label={`${t('inboundTripId', 'Inbound trip id')}`}
            variant='outlined'
            type='text'
            name={`inboundNumber`}
            data-cy='rail-visit-inbound-number'
            error={voyage.inboundNumber.isTouched && !!voyage.inboundNumber.error}
            helperText={
              voyage.inboundNumber.isTouched && !!voyage.inboundNumber.error
                ? tripIdHelperText(voyage.inboundNumber.error)
                : ''
            }
          />
        </Grid>
        <Grid size={{ xs: 5 }}>
          <TextField
            {...voyage.outboundNumber.props}
            onBlur={voyage.outboundNumber.validate}
            fullWidth
            label={`${t('outboundTripId', 'Outbound trip id')}`}
            variant='outlined'
            type='text'
            name={`outboundNumber`}
            data-cy='rail-visit-outbound-number'
            error={voyage.outboundNumber.isTouched && !!voyage.outboundNumber.error}
            helperText={
              voyage.outboundNumber.isTouched && !!voyage.outboundNumber.error
                ? tripIdHelperText(voyage.outboundNumber.error)
                : ''
            }
          />
        </Grid>
        <Grid size={{ xs: 2 }} display='flex' alignItems='center'>
          {onDelete && (
            <IconButton
              color='default'
              onClick={() => onDelete()}
              data-cy='rail-visit-delete-voyage-btn'
            >
              <BinIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Box>
  )
})

type RailVisitVoyageSubFormProps = { store: RailVisitGeneralInformationFormStore }

export const RailVisitVoyageSubForm = observer((props: RailVisitVoyageSubFormProps) => {
  const { store } = props

  const { t } = useTranslate()

  useEffect(
    () =>
      reaction(
        () =>
          store.voyages.value
            .map(v => `${v.inboundNumber.value}|${v.outboundNumber.value}`)
            .join('\n'),
        () => store.clearStaleDuplicateErrors(),
      ),
    [store],
  )

  return (
    <Stack gap={1}>
      {store.voyages.value.map((voyage: RailVisitVoyageTripIdType, index: number) => (
        <VoyageGroup
          key={index}
          index={index}
          voyage={voyage}
          onDelete={index > 0 ? () => store.removeVoyage(index) : undefined}
        />
      ))}
      <Box>
        <Button
          type='button'
          variant='text'
          color='primary'
          onClick={() => store.addVoyage()}
          startIcon={<PlusIcon />}
          data-cy='rail-visit-add-voyage-btn'
        >
          {t('add', 'Add')}
        </Button>
      </Box>
    </Stack>
  )
})
